import React, { useRef, useEffect, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { TrackingSession, TrackingSessionState } from '@trackthatride/realtime';
import { Ride, Driver } from '@trackthatride/core';

// Import MapLibre CSS (consumers need to import this in their app)
// import 'maplibre-gl/dist/maplibre-gl.css';

export interface TrackingMapProps {
  /** Tracking code for the delivery to track */
  trackingCode: string;
  
  /** Map container style */
  style?: React.CSSProperties;
  
  /** Map container className */
  className?: string;
  
  /** Initial map center coordinates [longitude, latitude] */
  center?: [number, number];
  
  /** Initial zoom level */
  zoom?: number;
  
  /** Map style URL (defaults to OpenStreetMap) */
  mapStyle?: string;
  
  /** Base URL for the API */
  apiBaseUrl?: string;
  
  /** Enable real-time tracking updates */
  enableRealtime?: boolean;
  
  /** Show route polyline between pickup and delivery */
  showRoute?: boolean;
  
  /** Custom marker icons */
  markers?: {
    pickup?: string;
    delivery?: string;
    driver?: string;
  };
  
  /** Callback when tracking state changes */
  onStateChange?: (state: TrackingSessionState) => void;
  
  /** Callback when map is loaded */
  onMapLoad?: (map: maplibregl.Map) => void;
  
  /** Callback for errors */
  onError?: (error: Error) => void;
}

const DEFAULT_STYLE = 'https://demotiles.maplibre.org/style.json';
const DEFAULT_CENTER: [number, number] = [0, 0];
const DEFAULT_ZOOM = 10;

export const TrackingMap: React.FC<TrackingMapProps> = ({
  trackingCode,
  style = { width: '100%', height: '400px' },
  className,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  mapStyle = DEFAULT_STYLE,
  apiBaseUrl,
  enableRealtime = true,
  showRoute = true,
  markers = {},
  onStateChange,
  onMapLoad,
  onError
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const trackingSession = useRef<TrackingSession | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [trackingState, setTrackingState] = useState<TrackingSessionState | null>(null);
  
  // Markers refs
  const pickupMarker = useRef<maplibregl.Marker | null>(null);
  const deliveryMarker = useRef<maplibregl.Marker | null>(null);
  const driverMarker = useRef<maplibregl.Marker | null>(null);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: center,
        zoom: zoom
      });

      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
      
      map.current.on('load', () => {
        setMapLoaded(true);
        if (onMapLoad && map.current) {
          onMapLoad(map.current);
        }
      });

      map.current.on('error', (e) => {
        console.error('MapLibre error:', e);
        if (onError) {
          onError(new Error(`Map error: ${e.error?.message || 'Unknown error'}`));
        }
      });

    } catch (error) {
      console.error('Failed to initialize map:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error('Map initialization failed'));
      }
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapStyle, center, zoom, onMapLoad, onError]);

  // Initialize tracking session
  useEffect(() => {
    if (!enableRealtime) return;

    try {
      trackingSession.current = new TrackingSession({
        baseUrl: apiBaseUrl,
        enableLogging: process.env.NODE_ENV === 'development'
      });

      const unsubscribe = trackingSession.current.subscribe((state) => {
        setTrackingState(state);
        if (onStateChange) {
          onStateChange(state);
        }
      });

      // Connect to tracking
      trackingSession.current.connect(trackingCode).catch((error) => {
        console.error('Failed to connect to tracking:', error);
        if (onError) {
          onError(error instanceof Error ? error : new Error('Tracking connection failed'));
        }
      });

      return () => {
        unsubscribe();
        if (trackingSession.current) {
          trackingSession.current.destroy();
          trackingSession.current = null;
        }
      };
    } catch (error) {
      console.error('Failed to initialize tracking session:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error('Tracking initialization failed'));
      }
    }
  }, [trackingCode, apiBaseUrl, enableRealtime, onStateChange, onError]);

  // Update markers when tracking state changes
  useEffect(() => {
    if (!mapLoaded || !map.current || !trackingState?.ride) return;

    updateMarkers(trackingState);
    fitMapToMarkers();
  }, [mapLoaded, trackingState]);

  const createMarkerElement = useCallback((type: 'pickup' | 'delivery' | 'driver', color: string): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = `tracking-marker tracking-marker-${type}`;
    
    if (markers[type]) {
      // Custom icon
      el.innerHTML = `<img src="${markers[type]}" alt="${type}" style="width: 32px; height: 32px;" />`;
    } else {
      // Default marker
      el.innerHTML = `
        <div style="
          width: 24px;
          height: 24px;
          background-color: ${color};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        ">
          ${type === 'pickup' ? 'P' : type === 'delivery' ? 'D' : 'V'}
        </div>
      `;
    }
    
    return el;
  }, [markers]);

  const updateMarkers = useCallback((state: TrackingSessionState) => {
    if (!map.current || !state.ride) return;

    const { ride, driver, currentLocation } = state;

    // Pickup marker
    if (ride.pickup_latitude && ride.pickup_longitude) {
      if (pickupMarker.current) {
        pickupMarker.current.remove();
      }
      
      pickupMarker.current = new maplibregl.Marker({
        element: createMarkerElement('pickup', '#4CAF50')
      })
        .setLngLat([ride.pickup_longitude, ride.pickup_latitude])
        .setPopup(new maplibregl.Popup().setHTML(`
          <div>
            <h4>Pickup Location</h4>
            <p>${ride.pickup_address}</p>
          </div>
        `))
        .addTo(map.current);
    }

    // Delivery marker
    if (ride.delivery_latitude && ride.delivery_longitude) {
      if (deliveryMarker.current) {
        deliveryMarker.current.remove();
      }
      
      deliveryMarker.current = new maplibregl.Marker({
        element: createMarkerElement('delivery', '#F44336')
      })
        .setLngLat([ride.delivery_longitude, ride.delivery_latitude])
        .setPopup(new maplibregl.Popup().setHTML(`
          <div>
            <h4>Delivery Location</h4>
            <p>${ride.delivery_address}</p>
            <p><strong>Customer:</strong> ${ride.customer_name}</p>
            <p><strong>Status:</strong> ${ride.status}</p>
          </div>
        `))
        .addTo(map.current);
    }

    // Driver marker (current location)
    if (currentLocation || (driver && driver.latitude && driver.longitude)) {
      const driverLat = currentLocation?.latitude || driver?.latitude;
      const driverLng = currentLocation?.longitude || driver?.longitude;
      
      if (driverLat && driverLng) {
        if (driverMarker.current) {
          driverMarker.current.remove();
        }
        
        driverMarker.current = new maplibregl.Marker({
          element: createMarkerElement('driver', '#2196F3')
        })
          .setLngLat([driverLng, driverLat])
          .setPopup(new maplibregl.Popup().setHTML(`
            <div>
              <h4>Driver Location</h4>
              ${driver ? `
                <p><strong>Name:</strong> ${driver.firstName} ${driver.lastName}</p>
                <p><strong>Vehicle:</strong> ${driver.vehicleType || 'N/A'}</p>
                <p><strong>Plate:</strong> ${driver.vehiclePlate || 'N/A'}</p>
              ` : ''}
              <p><strong>Last Update:</strong> ${state.lastUpdate ? new Date(state.lastUpdate).toLocaleTimeString() : 'Unknown'}</p>
            </div>
          `))
          .addTo(map.current);
      }
    }

    // Add route if enabled
    if (showRoute && ride.pickup_latitude && ride.pickup_longitude && 
        ride.delivery_latitude && ride.delivery_longitude) {
      addRoute(
        [ride.pickup_longitude, ride.pickup_latitude],
        [ride.delivery_longitude, ride.delivery_latitude]
      );
    }
  }, [createMarkerElement, showRoute]);

  const addRoute = useCallback((start: [number, number], end: [number, number]) => {
    if (!map.current) return;

    const routeId = 'delivery-route';
    
    // Remove existing route
    if (map.current.getSource(routeId)) {
      map.current.removeLayer(routeId);
      map.current.removeSource(routeId);
    }

    // Add simple straight line route (in production, you'd use a routing service)
    map.current.addSource(routeId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [start, end]
        }
      }
    });

    map.current.addLayer({
      id: routeId,
      type: 'line',
      source: routeId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#2196F3',
        'line-width': 3,
        'line-opacity': 0.7,
        'line-dasharray': [2, 2]
      }
    });
  }, []);

  const fitMapToMarkers = useCallback(() => {
    if (!map.current) return;

    const markers = [
      pickupMarker.current,
      deliveryMarker.current,
      driverMarker.current
    ].filter(Boolean);

    if (markers.length === 0) return;

    const bounds = new maplibregl.LngLatBounds();
    markers.forEach(marker => {
      if (marker) {
        bounds.extend(marker.getLngLat());
      }
    });

    map.current.fitBounds(bounds, {
      padding: 50,
      maxZoom: 15
    });
  }, []);

  return (
    <div
      ref={mapContainer}
      className={className}
      style={style}
      data-testid="tracking-map"
    />
  );
};

export default TrackingMap;